import React from 'react';
import { Handshake, CreditCard, Target, Briefcase, Home, PencilLine } from 'lucide-react';
import type { FormData } from '../../types/form';
import type { ReviewEditableField } from '../../routes/reviewEditController';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ActionLink from '../ui/ActionLink';
import Disclaimer from '../ui/Disclaimer';
import { DISCLAIMER_CONTENT } from '../../utils/disclaimerContent';
import { formatCurrency, getLoanPurpose, getEmploymentStatus, getPropertyStatus } from '../../utils/formatting';

type ApplicationReviewFormProps = {
  formData: FormData;
  onContinue: () => void;
  onEditField: (field: ReviewEditableField) => void;
  onEditAllDetails: () => void;
};

type ReviewRow = {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onEdit: () => void;
};

export default function ApplicationReviewForm({
  formData,
  onContinue,
  onEditField,
  onEditAllDetails,
}: ApplicationReviewFormProps) {
  const rows: ReviewRow[] = [
    {
      key: 'loan-amount',
      icon: CreditCard,
      label: 'Loan Amount',
      value: formData.loanAmount ? formatCurrency(formData.loanAmount) : 'Not specified',
      onEdit: () => onEditField('loanAmount'),
    },
    {
      key: 'loan-purpose',
      icon: Target,
      label: 'Loan Purpose',
      value: formData.loanPurpose ? getLoanPurpose(formData.loanPurpose) : 'Not specified',
      onEdit: () => onEditField('loanPurpose'),
    },
    {
      key: 'employment-status',
      icon: Briefcase,
      label: 'Employment Status',
      value: formData.employmentStatus ? getEmploymentStatus(formData.employmentStatus) : 'Not specified',
      onEdit: () => onEditField('employmentStatus'),
    },
    {
      key: 'property-status',
      icon: Home,
      label: 'Property Status',
      value: formData.propertyStatus ? getPropertyStatus(formData.propertyStatus) : 'Not specified',
      onEdit: () => onEditField('propertyStatus'),
    },
  ];

  return (
    <div id="application-review-form" className="space-y-6">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 text-sm font-medium text-[#212d52] bg-[#212d52]/5 rounded-full px-4 py-2">
          <Handshake className="w-4 h-4" />
          You're here from one of our trusted partners
        </div>

        <div>
          <Title
            id="application-review-title"
            name="application-review-title"
            as="h1"
            className="mb-2"
          >
            Let's finish your loan application
          </Title>
          <Description
            id="application-review-description"
            name="application-review-description"
            size="lg"
          >
            We've received your information from our partner. Please confirm or update your details below.
          </Description>
        </div>
      </div>

      <div id="application-review-details" className="bg-gray-50 rounded-lg p-6 text-left">
        <h3 className="font-semibold text-gray-900 mb-4">Your Application Details</h3>

        <div className="divide-y divide-gray-200">
          {rows.map(({ key, icon: Icon, label, value, onEdit }) => (
            <div
              key={key}
              id={`application-review-row-${key}`}
              className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Icon className="w-5 h-5 text-[#212d52] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-gray-600">{label}</div>
                  <div className="font-medium text-gray-900 truncate">{value}</div>
                </div>
              </div>

              <ActionLink
                id={`application-review-edit-${key}`}
                name={`application-review-edit-${key}`}
                onClick={onEdit}
                icon={PencilLine}
                align="right"
              >
                Edit
              </ActionLink>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button
          id="application-review-edit-details"
          name="application-review-edit-details"
          variant="outline"
          size="default"
          fullWidth
          onClick={onEditAllDetails}
        >
          Edit Details
        </Button>

        <Button
          id="application-review-continue"
          name="application-review-continue"
          variant="primary"
          size="default"
          fullWidth
          onClick={onContinue}
        >
          <span className="flex flex-col items-center leading-tight">
            <span>Continue</span>
          </span>
        </Button>
      </div>

      <Disclaimer
        id="application-review-disclaimer"
        name="application-review-disclaimer"
      >
        {DISCLAIMER_CONTENT}
      </Disclaimer>
    </div>
  );
}
